import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Sparkles, Loader2, ArrowLeft, ShoppingCart, Zap, Wand2, ZoomIn, Check } from "lucide-react";
import http from "@/api";
import { trackAction } from "@/behavior";
import { useApp } from "@/context";
import AddOnSuggestions from "@/components/AddOnSuggestions";
import PlaceInSpaceModal from "@/components/PlaceInSpaceModal";
import GiftScore from "@/components/GiftScore";
import ProductAIPreview from "@/components/ProductAIPreview";
import CompleteGift from "@/components/CompleteGift";
import LuxuryUpgrade from "@/components/LuxuryUpgrade";
import ShareButton from "@/components/ShareButton";
import ImageZoomModal from "@/components/ImageZoomModal";
import Seo from "@/components/Seo";
import NotifyMe from "@/components/NotifyMe";
import { BnplBadge } from "@/components/BnplBadge";
// خاصية الفيديو موقوفة — الصور فقط (ProductShowcaseVideo لم يعد مستخدماً)
import { BRAND } from "@/brand";

/**
 * Product detail — minimal, focused, conversion-first.
 * Only the essentials are shown above the fold: image, name, price, qty, BUY.
 * Optional features (AI bouquet, full description) are tucked into expandable
 * accordions to keep the screen calm and shoppable.
 */
export default function Product() {
  const { id } = useParams();
  const { t, lang, formatPrice, addToCart } = useApp();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [bouquet, setBouquet] = useState(null);
  const [bLoad, setBLoad] = useState(false);
  const [variantId, setVariantId] = useState(null);
  const [swatchIdxs, setSwatchIdxs] = useState([]);
  // iter#36: Map of group_id -> array of selected option_ids
  const [groupSelections, setGroupSelections] = useState({});
  const [showAI, setShowAI] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [showPlace, setShowPlace] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/products/${id}`);
        setProduct(data);
        try { trackAction("product_view", { product_id: data.id, product_name: data.name_ar || data.name_en }); } catch {}
        if (data?.variants?.length > 0) {
          const firstInStock = data.variants.find((v) => (v.stock || 0) > 0);
          setVariantId((firstInStock || data.variants[0]).id);
        }
      } catch { nav("/shop"); }
    })();
  }, [id, nav]);

  const variant = product?.variants?.find((v) => v.id === variantId) || null;
  const swatches = product?.color_swatches || [];
  // Multi-select colour swatches — an array of selected indices (Toggle).
  const selectedSwatches = swatchIdxs
    .map((i) => (swatches[i] ? { ...swatches[i], _idx: i } : null))
    .filter(Boolean);
  const lastSwatch = selectedSwatches.length ? selectedSwatches[selectedSwatches.length - 1] : null;
  const baseUnit = variant ? variant.price_sar : product?.price_sar;
  // iter#36: compute selected options price delta
  const optionGroups = product?.option_groups || [];
  const optionsDelta = optionGroups.reduce((sum, g) => {
    const picked = groupSelections[g.id] || [];
    return sum + (g.options || [])
      .filter((o) => picked.includes(o.id))
      .reduce((s, o) => s + Number(o.price_delta_sar || 0), 0);
  }, 0);
  const displayPrice = (Number(baseUnit) || 0) + optionsDelta;
  // Preview image follows the most-recently picked colour (falls back to variant/product).
  const displayImage = (lastSwatch && lastSwatch.image) ? lastSwatch.image
    : ((variant && variant.image) ? variant.image : product?.image);
  // AI product video (generated on demand from admin) — relative URLs share the app domain
  // خاصية الفيديو موقوفة بقرار المستخدم — الصور فقط (19 سبتمبر 2026)
  const vidSrc = null;
  const [showVideo, setShowVideo] = useState(true);
  const showingVideo = !!(vidSrc && showVideo);
  const displayStock = variant ? variant.stock : product?.stock;
  const stockMode = product?.stock_mode || "";
  const isOut = stockMode === "out" || (stockMode === "limited" && Number(displayStock ?? 0) <= 0);

  const swatchName = (s) => (lang === "ar" ? s.name_ar : s.name_en) || s.name_ar || s.name_en || "";

  const toggleSwatch = (i) => {
    setSwatchIdxs((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);
  };

  // Build the cart variants for the current selection: one cart line per chosen
  // colour (each carries its own name + image) so ALL selected colours reach
  // the cart — not just one. Falls back to the legacy single variant / null.
  const buildCartVariants = () => {
    if (selectedSwatches.length > 0) {
      return selectedSwatches.map((s) => ({
        id: `swatch-${s._idx}`,
        name: swatchName(s),
        price_sar: Number(baseUnit) || 0,
        image: s.image || product?.image,
      }));
    }
    return [variant];  // [null] when no variant → addToCart treats as base product
  };

  // Validate required groups
  const missingRequired = optionGroups
    .filter((g) => g.required && !(groupSelections[g.id] || []).length)
    .map((g) => lang === "ar" ? g.title_ar : g.title_en);

  const toggleOption = (group, optionId) => {
    setGroupSelections((prev) => {
      const cur = prev[group.id] || [];
      if (group.type === "single") {
        // radio: replace selection
        return { ...prev, [group.id]: cur.includes(optionId) ? [] : [optionId] };
      }
      // multi: toggle
      if (cur.includes(optionId)) {
        return { ...prev, [group.id]: cur.filter((x) => x !== optionId) };
      }
      return { ...prev, [group.id]: [...cur, optionId] };
    });
  };

  const buildSelectedOptionsPayload = () => {
    return Object.entries(groupSelections)
      .filter(([_, ids]) => (ids || []).length > 0)
      .map(([group_id, option_ids]) => ({ group_id, option_ids }));
  };

  const handleAdd = () => {
    if (!product || product.blocked) return;
    if (missingRequired.length > 0) {
      alert(lang === "ar"
        ? `الرجاء اختيار: ${missingRequired.join("، ")}`
        : `Please choose: ${missingRequired.join(", ")}`);
      return;
    }
    buildCartVariants().forEach((cv) => addToCart(product, qty, cv, buildSelectedOptionsPayload()));
    setAdded(true);
    setTimeout(() => setAdded(false), 8000);
  };

  // Quick checkout — add to cart and go straight to checkout (1-tap path)
  const handleBuyNow = () => {
    if (!product || product.blocked) return;
    if (missingRequired.length > 0) {
      alert(lang === "ar"
        ? `الرجاء اختيار: ${missingRequired.join("، ")}`
        : `Please choose: ${missingRequired.join(", ")}`);
      return;
    }
    buildCartVariants().forEach((cv) => addToCart(product, qty, cv, buildSelectedOptionsPayload()));
    nav("/checkout");
  };

  const suggestBouquet = async () => {
    if (!product) return;
    setShowAI(true);
    setBLoad(true);
    setBouquet(null);
    try {
      const { data } = await http.post("/ai/bouquet-suggest", {
        description: lang === "ar" ? product.description_ar : product.description_en,
      });
      setBouquet(data);
    } catch { setBouquet({ raw: lang === "ar" ? "تعذّر الاقتراح" : "Could not suggest" }); }
    finally { setBLoad(false); }
  };

  if (!product) return <div className="p-8 text-center opacity-60">...</div>;

  const name = lang === "ar" ? product.name_ar : product.name_en;
  const desc = lang === "ar" ? product.description_ar : product.description_en;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 pb-32">
      <Seo
        title={product.name_ar || product.name_en}
        description={(product.description_ar || product.description_en || "").slice(0, 160) || undefined}
        image={product.image && !String(product.image).startsWith("data:") ? product.image : undefined}
        type="product"
        url={`${window.location.origin}/p/${product.id}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name_ar || product.name_en,
          image: product.image && !String(product.image).startsWith("data:") ? [product.image] : undefined,
          description: product.description_ar || product.description_en || undefined,
          brand: { "@type": "Brand", name: "RARE GROUP" },
          offers: {
            "@type": "Offer",
            priceCurrency: "QAR",
            price: String(product.price_sar || 0),
            availability: (product.stock === 0) ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            url: `${window.location.origin}/p/${product.id}`,
          },
          ...(product.rating && product.rating_count ? {
            aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.rating_count },
          } : {}),
        }}
      />
      {/* ─── Top bar: back + share ─── */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => nav(-1)} className="chip" data-testid="back-btn">
          <ArrowLeft size={14} className={lang === "ar" ? "rtl-flip" : ""} />
          {lang === "ar" ? "رجوع" : "Back"}
        </button>
        <ShareButton product={product} variant="full" />
      </div>

      {/* ─── ONE clean product card ─── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="card-soft overflow-hidden">
        <button type="button" onClick={() => !showingVideo && displayImage && setZoomOpen(true)}
                className="relative block w-full aspect-[4/5] bg-gray-100 overflow-hidden"
                data-testid="product-image-zoom-trigger"
                aria-label={lang === "ar" ? "تكبير الصورة" : "Zoom image"}>
          {showingVideo ? (
            <video src={vidSrc} poster={displayImage} autoPlay muted loop playsInline
                   className="w-full h-full object-cover" data-testid="product-video" />
          ) : (
            <img src={displayImage} alt={name} className="w-full h-full object-cover" data-testid="product-main-image" />
          )}
          {vidSrc && (
            <span role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setShowVideo((v) => !v); }}
                  className="absolute top-3 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-white text-xs font-medium cursor-pointer"
                  style={{ background: "rgba(6,15,12,0.55)", backdropFilter: "blur(4px)", insetInlineEnd: 12 }}
                  data-testid="product-video-toggle">
              {showingVideo
                ? (lang === "ar" ? "🖼️ الصورة" : "🖼️ Photo")
                : (lang === "ar" ? "▶ الفيديو" : "▶ Video")}
            </span>
          )}
          {!showingVideo && (
            <span className="absolute bottom-3 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-white text-xs font-medium"
                  style={{ background: "rgba(6,15,12,0.55)", backdropFilter: "blur(4px)", insetInlineEnd: 12 }}>
              <ZoomIn size={14} /> {lang === "ar" ? "تكبير" : "Zoom"}
            </span>
          )}
          <span className="absolute bottom-3 flex items-center justify-center rounded-full w-9 h-9 shadow-sm pointer-events-none"
                style={{ background: "rgba(6,15,12,0.5)", backdropFilter: "blur(4px)", insetInlineStart: 12 }}
                data-testid="product-detail-rg-watermark">
            <img src={BRAND.logoWhite} alt="RG" className="w-[22px] h-[22px] object-contain" />
          </span>
        </button>

        <div className="p-4 sm:p-5 space-y-3">
          <h1 className={`${lang === "ar" ? "font-display-ar" : "font-display-en"} text-xl sm:text-2xl leading-tight`}
              data-testid="product-name">
            {name}
          </h1>

          <div className="flex items-baseline justify-between">
            <div className="price-display text-2xl font-bold" data-testid="product-display-price">
              {displayPrice > 0
                ? formatPrice(displayPrice)
                : (Number(product?.options_min_price) > 0
                  ? (lang === "ar" ? `يبدأ من ${formatPrice(product.options_min_price)}` : `From ${formatPrice(product.options_min_price)}`)
                  : formatPrice(displayPrice))}
            </div>
            <div className="text-xs opacity-60" data-testid="product-availability">
              {stockMode === "unlimited"
                ? <>✓ {lang === "ar" ? "متوفر" : "In stock"}</>
                : isOut
                  ? <span className="text-rose-600 font-bold">{lang === "ar" ? "نفد من المخزون" : "Out of stock"}</span>
                  : displayStock > 0
                    ? <>✓ {lang === "ar" ? `متوفر · ${displayStock}` : `In stock · ${displayStock}`}</>
                    : <span className="text-rose-600">{lang === "ar" ? "نفذ" : "Out of stock"}</span>}
            </div>
          </div>

          {/* 💳 شارة التقسيط — تظهر عند تفعيل مزوّد BNPL لدولة المتصفح */}
          <BnplBadge price={displayPrice || Number(product?.options_min_price) || 0} />

          {/* ⛔ موقوف مؤقتاً — شريط واضح + أبلغني عند التوفر */}
          {product.blocked && (
            <div className="rounded-2xl p-3 space-y-2 border"
                 style={{ background: "#fffbeb", borderColor: "#fcd34d" }}
                 data-testid="product-blocked-banner">
              <div className="font-black text-[13px]" style={{ color: "#b45309" }}>
                ⛔ {lang === "ar" ? "غير متوفر حالياً — سيعود قريباً" : "Currently unavailable — back soon"}
              </div>
              <NotifyMe productId={product.id} />
            </div>
          )}

          {/* 🚫 نفد من المخزون — شريط واضح + أبلغني عند التوفر */}
          {!product.blocked && isOut && (
            <div className="rounded-2xl p-3 space-y-2 border"
                 style={{ background: "#fef2f2", borderColor: "#fecaca" }}
                 data-testid="product-oos-banner">
              <div className="font-black text-[13px]" style={{ color: "#b91c1c" }}>
                🚫 {lang === "ar" ? "عذراً، المنتج نفد من المخزون" : "Sorry, this product is out of stock"}
              </div>
              <NotifyMe productId={product.id} />
            </div>
          )}

          {/* Colour swatches — multi-select circular picker; taps toggle & swap image */}
          {swatches.length > 0 && (
            <div className="pt-1" data-testid="product-color-swatches">
              <div className="text-xs opacity-70 mb-2 flex items-center flex-wrap gap-x-2 gap-y-1">
                <span>{lang === "ar" ? "الألوان (اختر واحداً أو أكثر)" : "Colours (pick one or more)"}</span>
                {selectedSwatches.length > 0 && (
                  <span className="font-semibold" style={{ color: "var(--primary)" }}
                        data-testid="product-swatch-selected-label">
                    · {selectedSwatches.map(swatchName).join("، ")}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2.5">
                {swatches.map((s, i) => {
                  const active = swatchIdxs.includes(i);
                  const label = swatchName(s);
                  return (
                    <button key={s.id || s._key || i} type="button"
                            onClick={() => toggleSwatch(i)}
                            title={label}
                            aria-label={label}
                            aria-pressed={active}
                            data-active={active ? "true" : "false"}
                            className="relative w-9 h-9 rounded-full transition-all active:scale-95 flex items-center justify-center"
                            style={{
                              background: s.hex || "#e5e7eb",
                              boxShadow: active
                                ? "0 0 0 2px #fff, 0 0 0 4px var(--primary)"
                                : "0 0 0 1px rgba(0,0,0,0.15)",
                            }}
                            data-testid={`product-swatch-${i}`}>
                      {active && (
                        <span className="text-white drop-shadow"
                              style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.6))" }}>
                          <Check size={16} strokeWidth={3} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Variants (only when present) */}
          {product.variants && product.variants.length > 0 && (
            <div className="pt-2" data-testid="product-variants">
              <div className="text-xs opacity-70 mb-2">{lang === "ar" ? "اختر الخيار" : "Choose option"}</div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => {
                  const active = v.id === variantId;
                  const oos = (v.stock || 0) <= 0;
                  return (
                    <button key={v.id} type="button" disabled={oos}
                            onClick={() => setVariantId(v.id)}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl border transition-all ${active ? "border-emerald-700 bg-emerald-50" : "border-transparent bg-black/5 hover:bg-black/10"} ${oos ? "opacity-40 cursor-not-allowed line-through" : ""}`}
                            data-testid={`variant-pick-${v.id}`}>
                      {v.image && <img src={v.image} alt="" className="w-6 h-6 rounded-md object-cover" />}
                      <div className="text-start">
                        <div className="text-xs font-medium leading-tight">{v.name}</div>
                        <div className="text-[11px] price-display leading-tight">{formatPrice(v.price_sar)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Iter#36 — Option groups (wrapping, add-ons, quantity selectors etc.) */}
          {optionGroups.length > 0 && (
            <div className="pt-1 space-y-4" data-testid="product-option-groups">
              {optionGroups.map((g) => {
                const picked = groupSelections[g.id] || [];
                const title = lang === "ar" ? g.title_ar : g.title_en;
                return (
                  <div key={g.id} className="border-t pt-3" style={{ borderColor: "var(--border)" }}
                       data-testid={`opt-group-${g.id}`}>
                    <div className="text-sm font-bold mb-2 flex items-baseline justify-between">
                      <span>
                        {title}
                        {g.required && <span className="ms-1 text-rose-600 text-xs font-medium">
                          ({lang === "ar" ? "مطلوب" : "required"})
                        </span>}
                      </span>
                      <span className="text-[10px] opacity-50 uppercase tracking-widest">
                        {lang === "ar" ? "السعر" : "Price"}
                      </span>
                    </div>
                    <div className="divide-y" style={{ borderColor: "var(--border)" }}>
                      {(g.options || []).map((o) => {
                        const isPicked = picked.includes(o.id);
                        const oos = o.stock !== undefined && o.stock !== null && Number(o.stock) <= 0;
                        const label = lang === "ar" ? o.label_ar : o.label_en;
                        const delta = Number(o.price_delta_sar || 0);
                        return (
                          <button key={o.id} type="button"
                                  disabled={oos}
                                  onClick={() => toggleOption(g, o.id)}
                                  className={`w-full flex items-center gap-3 py-2.5 transition-colors ${isPicked ? "bg-emerald-50/70" : "hover:bg-black/5"} ${oos ? "opacity-40 cursor-not-allowed" : ""}`}
                                  data-testid={`opt-pick-${g.id}-${o.id}`}>
                            <span className={`shrink-0 w-5 h-5 ${g.type === "multi" ? "rounded" : "rounded-full"} border-2 flex items-center justify-center transition-all ${isPicked ? "bg-emerald-700 border-emerald-700" : ""}`}
                                  style={{ borderColor: isPicked ? "" : "var(--border)" }}>
                              {isPicked && (
                                g.type === "multi"
                                  ? <span className="text-white text-[10px] leading-none">✓</span>
                                  : <span className="w-2 h-2 rounded-full bg-white" />
                              )}
                            </span>
                            {o.image && <img src={o.image} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" />}
                            <span className="flex-1 text-sm text-start font-medium">
                              {label}
                              {oos && <span className="ms-2 text-xs text-rose-600">{lang === "ar" ? "(نفذ)" : "(out)"}</span>}
                            </span>
                            <span className={`text-sm shrink-0 ${delta > 0 ? "opacity-80 font-medium" : "opacity-40"}`}>
                              {delta > 0 ? `+ ${formatPrice(delta)}` : (lang === "ar" ? "بدون إضافة" : "+ 0")}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Qty selector + "see it in your space" — one compact row */}
          <div className="flex items-center gap-3 pt-1">
            <span className="text-sm opacity-70">{lang === "ar" ? "الكمية" : "Qty"}</span>
            <div className="flex items-center gap-1 border rounded-full p-1" style={{ borderColor: "var(--border)" }}>
              <button className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center"
                      onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrease">
                <Minus size={14} />
              </button>
              <div className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</div>
              <button className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center"
                      onClick={() => setQty(Math.min(displayStock || qty + 1, qty + 1))} data-testid="qty-increase">
                <Plus size={14} />
              </button>
            </div>
            <button
              onClick={() => setShowPlace(true)}
              className="ms-auto flex items-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-semibold transition-all shadow-sm active:scale-[0.97] hover:opacity-90"
              style={{ background: "var(--primary)", color: "var(--on-primary)" }}
              data-testid="place-in-space-btn">
              <Wand2 size={14} />
              {lang === "ar" ? "شاهده في مكانك" : "In your space"}
            </button>
          </div>

          {/* 🎨 تغيير ألوان المنتج بالذكاء — ظاهر دائماً (طلب المستخدم) */}
          <ProductAIPreview productId={product.id} baseImage={displayImage} lang={lang} product={product} />

          {/* 🎁 أكمل هديتك — اقتراحات متناسقة تلقائياً */}
          <CompleteGift product={product} lang={lang} />
        </div>
      </motion.div>

      {/* ─── Sticky bottom action bar — always visible, conversion-first ───
           فجوة أمان 10px فوق الشريط السفلي: اللمسة الواقعة بينهما تسقط على
           المحتوى الخامل بدل تبويب «اربح مع RG» — وطبقة z أعلى من الشريط. */}
      <div className="fixed inset-x-0 z-50 bg-white/95 backdrop-blur border-t shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.15)] px-4 pt-2 pb-3"
           style={{ bottom: "calc(var(--bnav-total) + 10px)" }}
           onPointerDown={(e) => e.stopPropagation()}
           data-testid="product-cta-bar">
        {/* درع الفجوة: يبتلع أي لمسة بين شريط الشراء والشريط السفلي — لا تتسرب للمحتوى خلفهما */}
        <div className="absolute inset-x-0 top-full" style={{ height: 10 }} aria-hidden="true" />
        <div className="max-w-2xl mx-auto flex gap-2">
          {(product.blocked || isOut) ? (
            <NotifyMe productId={product.id} />
          ) : (
          <>
          <button onClick={handleAdd}
                  className="btn-outline flex-1 !min-h-0 !py-2.5 !text-[13px] !px-2"
                  data-testid="add-to-cart-btn">
            <ShoppingCart size={16} />
            {added ? `✓ ${t("added")}` : t("addToCart")}
          </button>
          <button onClick={handleBuyNow}
                  className="btn-primary flex-1 !min-h-0 !py-2.5 !text-[13px] !px-2"
                  data-testid="buy-now-btn">
            <Zap size={16} />
            {lang === "ar" ? "اشترِ الآن" : "Buy now"}
          </button>
          </>
          )}
        </div>
      </div>

      {/* ─── Add-on suggestions: balloons, cake, chocolate, money ─── */}
      <AddOnSuggestions productId={product.id} />

      {/* ─── AI extras: gift score + luxury upgrade (collapsed) ─── */}
      <details className="mt-4 card-soft" data-testid="product-ai-extras">
        <summary className="px-4 py-3 cursor-pointer select-none font-semibold text-sm flex justify-between items-center">
          <span className="flex items-center gap-2">
            <Sparkles size={14} /> {lang === "ar" ? "مزايا ذكية — تقييم الهدية · ترقية أفخم" : "Smart features — gift score · upgrade"}
          </span>
          <span className="opacity-50">+</span>
        </summary>
        <div className="px-3 pb-4 space-y-3">
          <GiftScore productId={product.id} lang={lang} />
          <LuxuryUpgrade productId={product.id} lang={lang} formatPrice={formatPrice} />
        </div>
      </details>

      {/* ─── Optional: description (collapsed by default) ─── */}
      {desc && (
        <details className="mt-4 card-soft" data-testid="product-desc">
          <summary onClick={() => setShowDesc((v) => !v)}
                   className="px-4 py-3 cursor-pointer select-none font-semibold text-sm flex justify-between items-center">
            {lang === "ar" ? "تفاصيل المنتج" : "Product details"}
            <span className="opacity-50">{showDesc ? "−" : "+"}</span>
          </summary>
          <div className="px-4 pb-4 text-sm opacity-80 leading-relaxed">{desc}</div>
        </details>
      )}

      {/* ─── Optional: AI bouquet suggestion (collapsed) ─── */}
      <details className="mt-2 card-soft" data-testid="product-ai-section">
        <summary className="px-4 py-3 cursor-pointer select-none font-semibold text-sm flex justify-between items-center">
          <span className="flex items-center gap-2">
            <Sparkles size={14} /> {t("aiBouquet")}
          </span>
          <span className="opacity-50">+</span>
        </summary>
        <div className="px-4 pb-4 space-y-3">
          <button onClick={suggestBouquet} disabled={bLoad}
                  className="btn-outline w-full !text-sm"
                  data-testid="ai-bouquet-btn">
            {bLoad ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
            {lang === "ar" ? "اقتراح باقة مشابهة" : "Suggest similar bouquet"}
          </button>
          {bouquet && (
            <div className="text-sm space-y-2" data-testid="bouquet-result">
              {bouquet.bouquet_name && <div className="font-semibold">{bouquet.bouquet_name}</div>}
              {bouquet.description && <div className="opacity-80">{bouquet.description}</div>}
              {bouquet.main_flowers && (
                <div className="flex flex-wrap gap-1">
                  {bouquet.main_flowers.map((f, i) => <span key={`${f}-${i}`} className="chip text-xs">{f}</span>)}
                </div>
              )}
              {bouquet.raw && !bouquet.bouquet_name && (
                <pre className="whitespace-pre-wrap text-xs">{bouquet.raw}</pre>
              )}
            </div>
          )}
        </div>
      </details>

      <PlaceInSpaceModal
        open={showPlace}
        onClose={() => setShowPlace(false)}
        product={product}
        lang={lang}
        addToCart={addToCart}
        formatPrice={formatPrice}
      />

      <ImageZoomModal
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        src={displayImage}
        alt={name}
        lang={lang}
      />
    </div>
  );
}
